'use client';

import { Button } from '@common/components';
import { SvgChevrondown } from '@common/icon';
import { cn } from '@core/utils';
import type { ListChildcarePolicyDto } from '@generated/unauth';
import { useEffect, useRef, useState } from 'react';

import { Image } from '@/components/client';

interface PolicyBudgetGroupProps {
  agency: string;
  icon?: string;
  policyBudgetList: ListChildcarePolicyDto[];
  summary: number;
  isLastGroup: boolean;
}

export const PolicyBudgetGroup = ({
  agency,
  icon,
  policyBudgetList,
  summary,
  isLastGroup,
}: PolicyBudgetGroupProps) => {
  const [openedAccordionPolicyName, setOpenedAccordionPolicyName] =
    useState<string>();

  return (
    <article className="flex flex-col gap-4">
      <section className="flex items-start justify-between py-12 pr-4">
        <section className="flex items-center gap-4">
          <Image alt="기관 아이콘" height={20} src={icon ?? ''} width={20} />
          <p className="font-600 text-Gray-black typo-body1">{agency}에서</p>
        </section>
        <p className="font-600 text-B-500 typo-body2">
          총 {Math.floor(summary / 10000).toLocaleString()}만원
        </p>
      </section>
      <ul className="flex flex-col">
        {policyBudgetList.map((policyBudget, index) => (
          <li key={policyBudget.name}>
            <PolicyAccordion
              isHideBorderBottom={
                index === policyBudgetList.length - 1 && isLastGroup
              }
              isOpen={openedAccordionPolicyName === policyBudget.name}
              policyBudget={policyBudget}
              onToggle={() => {
                setOpenedAccordionPolicyName(
                  openedAccordionPolicyName === policyBudget.name
                    ? undefined
                    : policyBudget.name,
                );
              }}
            />
          </li>
        ))}
      </ul>
    </article>
  );
};

interface PolicyAccordionProps {
  isOpen: boolean;
  onToggle: () => void;
  policyBudget: ListChildcarePolicyDto;
  isHideBorderBottom: boolean;
}

const PolicyAccordion = ({
  isOpen,
  onToggle,
  policyBudget,
  isHideBorderBottom,
}: PolicyAccordionProps) => {
  return (
    <dl
      className={cn('flex flex-col', {
        'gap-16 duration-300': isOpen,
        'gap-0 duration-300': !isOpen,
      })}
    >
      <dt>
        <button
          type="button"
          className={cn(
            'flex w-full items-center justify-between border-b border-Gray-300 py-16 pl-12',
            {
              'border-b-0': isHideBorderBottom,
            },
          )}
          onClick={onToggle}
        >
          <span className="font-500 text-Gray-800 typo-body3">
            {policyBudget.name}
          </span>
          <section className="flex items-center gap-9">
            <span className="font-600 text-Gray-900 typo-body3">
              {Math.floor(policyBudget.supportAmount / 10000).toLocaleString()}
              만원
            </span>
            <SvgChevrondown
              className={`size-20 text-Gray-500 transition-transform duration-300 ${
                isOpen ? 'rotate-180' : ''
              }`}
            />
          </section>
        </button>
      </dt>
      <AccordionContent isOpen={isOpen}>
        <section className="flex flex-col gap-24 rounded-small bg-Gray-100 p-16 font-400 text-Gray-800 typo-body4">
          <dl className="flex flex-col gap-4">
            <dt className="font-700">금액</dt>
            {policyBudget.supportAmountDetail ? (
              Object.entries(policyBudget.supportAmountDetail).map(
                ([key, value]) => (
                  <dd key={key} className="flex items-center justify-between">
                    <p>{key}</p>
                    <p className="font-600 text-Gray-900">
                      월 {Math.floor(Number(value) / 10000).toLocaleString()}
                      만원
                    </p>
                  </dd>
                ),
              )
            ) : (
              <dd className="whitespace-pre-line">
                {Math.floor(
                  policyBudget.supportAmount / 10000,
                ).toLocaleString()}
                만원
                {policyBudget.note ? <p>({policyBudget.note})</p> : null}
              </dd>
            )}
          </dl>
          <dl className="flex flex-col gap-4">
            <dt className="font-700">지원대상</dt>
            <dd className="whitespace-pre-line">{policyBudget.descTarget}</dd>
          </dl>
          <dl className="flex flex-col gap-4">
            <dt className="font-700">지원내용</dt>
            <dd className="whitespace-pre-line">{policyBudget.descSupport}</dd>
          </dl>
          <dl className="flex flex-col gap-4">
            <dt className="font-700">관련정보</dt>
            <dd>
              <dl>
                <dt>○ 소관기관</dt>
                <dd className="whitespace-pre-line">{`  - ${policyBudget.agency}`}</dd>
              </dl>

              {policyBudget.department || policyBudget.agencyTel ? (
                <dl className="pt-12">
                  <dt>○ 문의처</dt>
                  <dd className="whitespace-pre-line">
                    {policyBudget.department ? (
                      <div>{`  - ${policyBudget.department}`}</div>
                    ) : null}
                    {policyBudget.agencyTel ? (
                      <div>{`  - 전화번호 : ${policyBudget.agencyTel}`}</div>
                    ) : null}
                  </dd>
                </dl>
              ) : null}
            </dd>
          </dl>

          <dl className="flex flex-col gap-4">
            <dt className="font-700">신청방법</dt>
            <dd className="whitespace-pre-line">{policyBudget.descApply}</dd>
          </dl>
        </section>
        <Button
          asChild
          className="w-full"
          dimensions="H48"
          disabled={!policyBudget.infoUrl}
          theme="sub-line"
        >
          <a
            aria-label="외부 링크로 이동"
            href={policyBudget.infoUrl}
            rel="noreferrer"
            target="_blank"
          >
            홈페이지 직접 확인하기
          </a>
        </Button>
      </AccordionContent>
    </dl>
  );
};

interface AccordionContentProps {
  isOpen: boolean;
  children: React.ReactNode;
}

const AccordionContent = ({ isOpen, children }: AccordionContentProps) => {
  const contentRef = useRef<HTMLElement>(null);
  const [height, setHeight] = useState<number>(0);

  useEffect(() => {
    if (isOpen) {
      setHeight((contentRef.current?.scrollHeight ?? 0) + 16);
    } else {
      setHeight(0);
    }
  }, [isOpen]);

  return (
    <dd
      style={{
        height: height,
        overflow: 'hidden',
        transition: 'height 0.3s ease-in-out',
      }}
    >
      <section ref={contentRef} className="flex flex-col gap-16 pb-16">
        {children}
      </section>
    </dd>
  );
};
